import { useState, useEffect } from "react";
import "react-day-picker/dist/style.css";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "ui/card";
import { Button } from "ui/button";
import { InputFieldset } from "ui/inputFieldset";
import { SelectFieldset } from "ui/selectFieldset";
import { SelectTriggerUI } from "ui/selectTrigger";
import { Label } from "ui/label";
import { Badge } from "ui/badge";
import {
  ArrowLeft,
  Save,
  Plane,
  MapPin,
  Plus,
  Trash2,
  Truck,
  Ship,
  ArrowRight,
  PencilLine,
  BookOpenCheck,
  ShieldCheck,
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectValue } from "ui/select";
import { RadioGroup, RadioGroupItem } from "ui/radio-group";
import { Switch } from "ui/switch";
import { toast } from "sonner";
import { FullScreenLoading } from "components/ui/loader";
import {
  BufferConfig,
  getBufferConfigurations,
  getVenues,
  VenueApi,
  PortApi,
  getPorts,
  getCutoffConfigurations,
  CutoffConfig,
  getPortByPublicId,
  getTourOperatorWithDocuments,
  IntercityCheckoutApi,
  getIntercityCheckouts,
  getHotelsByVenue,
  HotelPropertyApi,
} from "services/tourService";
import { mapTypeFromApi } from "utils/portTypeMapper";
import { CustomDayPicker } from "ui/CustomDayPicker";
import { createTour, getGeneralMasterByCode } from "services/tourService";
import { useAuth } from "contexts/AuthContext";
import { usePageHeader } from "contexts/PageHeaderContext";
import {
  SwitchableTourCategory,
  TourTypeSwitcher,
} from "./components/TourTypeSwitcher";
import {
  calculateVenueSchedule,
  getIntercityCheckout,
  getTotalVenueBuffer,
  getTravelTimeMinutes,
  normalizeTime,
  parseTimeToMinutes,
  canAccommodateAnotherVenue,
} from "utils/venueScheduleCalculator";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "ui/accordion";

interface CreateTourMiqaatPageProps {
  onNavigate?: (page: string) => void;
}

interface FormData {
  tourType: string;
  packageId?: string;
  packageName?: string;
  arrivalTransportMode: "flight" | "road" | "sea";
  arrivalAirport: number;
  arrivalDate: string;
  arrivalTime: string;
  arrivalFlightNumber: string;
  arrivalBookingRef: string;
  arrivalAirline: string;
  arrivalConfirmed: boolean;
  departureTransportMode: "flight" | "road" | "sea";
  departureAirport: number;
  departureDate: string;
  departureTime: string;
  departureFlightNumber: string;
  departureBookingRef: string;
  departureAirline: string;
  departureConfirmed: boolean;
  tourName: string;
  parentTourId: string;
  noOfApplicants: string;
  faizAccommodation: "yes" | "no";
  accommodationPreference: "minimum" | "maximum";
  specialAccommodationRequest: string;
  termsAndConditions: string;
  notes: string;
  venues: Array<{
    id: string;
    venueId: number | null;
    hotelPropertyId: number | null;
    hotelName: string;
    fromDate: string;
    fromTime: string;
    toDate: string;
    toTime: string;
  }>;
}

enum TourType {
  GENERAL = 1,
  MIQAAT = 2,
}

export default function CreateTourMiqaatPage({
  onNavigate,
}: CreateTourMiqaatPageProps) {
  const navigate = useNavigate();
  const { setPageHeader } = usePageHeader();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [venuesFromApi, setVenuesFromApi] = useState<VenueApi[]>([]);
  const [buffers, setBuffers] = useState<BufferConfig[]>([]);
  const [ports, setPorts] = useState<PortApi[]>([]);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [cutoffs, setCutoffs] = useState<CutoffConfig[]>([]);
  const [cutoffDates, setCutoffDates] = useState<string[]>([]);

  const [arrivalCalendarMonth, setArrivalCalendarMonth] = useState<
    Date | undefined
  >(undefined);
  const [departureCalendarMonth, setDepartureCalendarMonth] = useState<
    Date | undefined
  >(undefined);
  const [venueCalendarMonths, setVenueCalendarMonths] = useState<
    Record<string, Date | undefined>
  >({});

  const [isVenueManuallyChanged, setIsVenueManuallyChanged] = useState(false);
  const [portBlocks, setPortBlocks] = useState<Record<number, Date[]>>({});
  const [operatorExpiryDate, setOperatorExpiryDate] = useState<Date | null>(
    null,
  );
  const [accommodateBy, setAccommodateBy] = useState<"minimum" | "maximum">(
    "maximum",
  );
  const [specialRoomRequestDetails, setSpecialRoomRequestDetails] =
    useState<string>("");
  const [loading, setLoading] = useState(false);

  const [hotelsByVenue, setHotelsByVenue] = useState<
    Record<number, HotelPropertyApi[]>
  >({});

  useEffect(() => {
    setPageHeader({
      title: "Create New Tour",
      icon: <MapPin className="h-4 w-4" />,
      onBack: () => navigate("/tours/create-dashboard"),
    });

    return () => setPageHeader(null);
  }, [navigate, setPageHeader]);

  const [termsAndConditions, setTermsAndConditions] = useState("");

  const portBlockedDates = Object.values(portBlocks).flat();

  const blockedDateReasons = Object.entries(portBlocks).flatMap(
    ([portId, dates]) => {
      const port = ports.find((p) => p.portId === Number(portId));

      return dates.map((date) => ({
        date,
        label: `${port?.portName || "Port"} block`,
      }));
    },
  );

  const cutoffBlockedDates = cutoffDates.map((d) => {
    const [y, m, day] = d.split("-").map(Number);
    return new Date(y, m - 1, day);
  });

  const allBlockedDates = portBlockedDates;

  const tourOperatorId = user?.tourOperatorId ?? null;

  const location = useLocation();
  const selectedMiqaat = location.state?.selectedMiqaat;

  const flowType = location.state?.flowType;

  const [intercityCheckouts, setIntercityCheckouts] = useState<
    IntercityCheckoutApi[]
  >([]);

  const [termsAccepted, setTermsAccepted] = useState(false);

  const isMiqaat = selectedMiqaat?.type === "package";
  const isRequestFlow = flowType === "request";
  const isExternalFlow = flowType === "external";

  const isFaizReadOnly = isRequestFlow || isExternalFlow;
  const selectedTourCategory: SwitchableTourCategory = isRequestFlow
    ? 3
    : isExternalFlow
      ? 4
      : 1;

  const handleTourTypeChange = (category: SwitchableTourCategory) => {
    if (category === 1) {
      navigate("/tours/tour-create/instant", {
        state: { tourCategory: 1 },
      });
      return;
    }

    navigate("/tours/tour-create", {
      replace: true,
      state: {
        selectedMiqaat: { type: "general" },
        flowType: category === 3 ? "request" : "external",
        tourCategory: category,
      },
    });
  };

  useEffect(() => {
    setPageHeader({
      title: "Create New Tour",
      icon: <MapPin className="h-4 w-4" />,
      onBack: () => navigate("/tours/create-dashboard"),
    });

    return () => setPageHeader(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, setPageHeader]);

  const [formData, setFormData] = useState<FormData>({
    tourType: "",
    arrivalTransportMode: "flight",
    arrivalAirport: 0,
    arrivalDate: "",
    arrivalTime: "",
    arrivalFlightNumber: "",
    arrivalBookingRef: "",
    arrivalAirline: "",
    arrivalConfirmed: false,
    departureTransportMode: "flight",
    departureAirport: 0,
    departureDate: "",
    departureTime: "",
    departureFlightNumber: "",
    departureBookingRef: "",
    departureAirline: "",
    departureConfirmed: false,
    tourName: "",
    parentTourId: "",
    noOfApplicants: "",
    faizAccommodation: "no",
    termsAndConditions: "",
    notes: "",
    accommodationPreference: "maximum",
    specialAccommodationRequest: "",
    venues: [
      {
        id: "venue-1",
        venueId: null,
        hotelPropertyId: null,
        hotelName: "",
        fromDate: "",
        fromTime: "",
        toDate: "",
        toTime: "",
      },
      {
        id: "venue-2",
        venueId: null,
        hotelPropertyId: null,
        hotelName: "",
        fromDate: "",
        fromTime: "",
        toDate: "",
        toTime: "",
      },
    ],
  });

  // const getTourType = () => {
  //   return selectedMiqaat?.type === "package" ? 1 : 2;
  // };

  const getTourType = () => {
    if (selectedMiqaat?.type === "package") {
      return TourType.MIQAAT;
    }

    return TourType.GENERAL;
  };

  const getRotationId = () => {
    if (selectedMiqaat?.type === "package") {
      return selectedMiqaat.rotationId;
    }

    return 1;
  };

  const getTourCategory = () => {
    if (selectedMiqaat?.type === "package") {
      return 2; // Miqaat
    }

    if (isRequestFlow) {
      return 3; // Faiz
    }

    if (isExternalFlow) {
      return 4; // External
    }

    return 1; // Instant
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      await loadTermsAndConditions();

      if (!tourOperatorId) {
        toast.error("Tour operator not found");
        return;
      }

      if (
        formData.parentTourId.trim() &&
        !isValidParentTourId(formData.parentTourId.trim())
      ) {
        setErrors((prev) => ({
          ...prev,
          parentTourId:
            "Only letters, numbers and hyphens (-) are allowed. Maximum 50 characters.",
        }));

        toast.error("Please enter a valid Parent Tour Id");
        return;
      }

      console.log("RotationId being sent:", getRotationId());

      const payload = {
        parent_id: formData.parentTourId.trim() || null,
        tourName: formData.tourName,
        tourType: getTourType(),
        tourCategory: getTourCategory(),
        rotationId: getRotationId(),
        tourOperatorId: tourOperatorId,
        noOfApplicants: Number(formData.noOfApplicants),
        faizAccommodation: formData.faizAccommodation === "yes",
        ...(formData.faizAccommodation === "yes" && {
          accommodationPreference: accommodateBy === "minimum" ? 1 : 2,
          specialAccommodationRequest: specialRoomRequestDetails.trim(),
        }),
        termsAndConditions: formData.termsAndConditions,
        notes: formData.notes,
        arrivalTransportMode: mapTransportMode(formData.arrivalTransportMode),
        arrivalFlightNo: formData.arrivalFlightNumber || "",
        arrivalTime: `${formData.arrivalTime}:00`,
        arrivalPortId: formData.arrivalAirport,
        arrivalDate: toISOStringWithTime(
          formData.arrivalDate,
          formData.arrivalTime,
        ),
        arrivalBookingRef: formData.arrivalBookingRef || "",
        arrivalAirline: formData.arrivalAirline || "",

        departureTransportMode: mapTransportMode(
          formData.departureTransportMode,
        ),
        departureFlightNo: formData.departureFlightNumber || "",
        departureTime: `${formData.departureTime}:00`,
        departurePortId: formData.departureAirport,
        departureDate: toISOStringWithTime(
          formData.departureDate,
          formData.departureTime,
        ),
        departureBookingRef: formData.departureBookingRef || "",
        departureAirline: formData.departureAirline || "",

        isArrivalConfirmed: formData.arrivalConfirmed,
        isDepartureConfirmed: formData.departureConfirmed,

        venueSchedules: formData.venues
          .filter((v): v is typeof v & { venueId: number } => v.venueId != null)
          .map((v) => ({
            venueId: v.venueId,
            ...(formData.faizAccommodation === "no" && {
              ...(v.hotelPropertyId
                ? {
                    hotelPropertyId: v.hotelPropertyId,
                    hotelName: v.hotelName,
                  }
                : {}),
            }),
            checkInDate: toISOStringWithTime(v.fromDate, v.fromTime),
            checkInTime: `${v.fromTime}:00`,
            checkOutDate: toISOStringWithTime(v.toDate, v.toTime),
            checkOutTime: `${v.toTime}:00`,
          })),

        createdBy: Number(user?.id),
      };

      const res = await createTour(payload);

      toast.success(`Tour created successfully! Tour Code: ${res.tourCode}`);
      navigate("/tours");
    } catch (error) {
      console.error(error);
      toast.error("Failed to create tour");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadHotels = async () => {
      const result: Record<number, HotelPropertyApi[]> = {};

      for (const venue of formData.venues) {
        if (!venue.venueId) continue;

        const selectedVenue = venuesFromApi.find(
          (v) => v.venueId === venue.venueId,
        );

        if (!selectedVenue) continue;

        try {
          result[venue.venueId] = await getHotelsByVenue(
            selectedVenue.publicId,
          );
        } catch (err) {
          console.error(err);
        }
      }

      setHotelsByVenue(result);
    };

    loadHotels();
  }, [formData.venues, venuesFromApi]);

  useEffect(() => {
    if (isRequestFlow) {
      setFormData((prev) => ({
        ...prev,
        faizAccommodation: "yes",
      }));
    }

    if (isExternalFlow) {
      setFormData((prev) => ({
        ...prev,
        faizAccommodation: "no",
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowType]);

  useEffect(() => {
    if (isMiqaat) {
      setFormData((prev) => ({
        ...prev,
        faizAccommodation: "yes",
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMiqaat]);

  useEffect(() => {
    if (formData.venues.some((v) => !v.venueId)) return;
    if (isVenueManuallyChanged) return;

    if (
      !isValidTime(formData.arrivalTime) ||
      !isValidTime(formData.departureTime)
    ) {
      return;
    }

    const updatedVenues = calculateVenueSchedule({
      venues: formData.venues,
      arrivalDate: formData.arrivalDate,
      arrivalTime: formData.arrivalTime,
      departureDate: formData.departureDate,
      departureTime: formData.departureTime,
      buffers,
      intercityCheckouts,
      tourType: getTourType(),
    });

    if (!updatedVenues) return;

    const isSame =
      JSON.stringify(formData.venues) === JSON.stringify(updatedVenues);

    if (isSame) return;

    setFormData((prev) => ({
      ...prev,
      venues: prev.venues.map((v, i) => ({
        ...v, // keep id
        ...updatedVenues[i], // override calculated fields
      })),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.arrivalDate,
    formData.arrivalTime,
    formData.departureDate,
    formData.departureTime,
    buffers,
    intercityCheckouts,
    formData.venues,
  ]);

  const loadTermsAndConditions = async () => {
    try {
      let code = "";

      const category = getTourCategory();

      switch (category) {
        case 2:
          code = "TOUR_TNC_Miqaat";
          break;

        case 3:
          code = "TOUR_TNC_Faiz";
          break;

        case 4:
          code = "TOUR_TNC_Hotel";
          break;

        default:
          code = "TOUR_TNC_Instant";
          break;
      }

      const res = await getGeneralMasterByCode(code);

      setTermsAndConditions(res.value);

      setFormData((prev) => ({
        ...prev,
        termsAndConditions: res.value,
      }));
    } catch (error) {
      console.error("Failed to load T&C", error);
    }
  };

  useEffect(() => {
    if (currentStep !== 3) return;

    loadTermsAndConditions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  useEffect(() => {
    const fetchBuffers = async () => {
      try {
        const data = await getBufferConfigurations();
        setBuffers(data.filter((b) => b.isActive));
      } catch (e) {
        console.error("Buffer fetch failed", e);
      }
    };

    fetchBuffers();
  }, []);

  useEffect(() => {
    const fetchVenues = async () => {
      try {
        const data = await getVenues();
        setVenuesFromApi(data.filter((v) => v.isActive));
      } catch (e) {
        console.error("Venue fetch failed", e);
      }
    };

    fetchVenues();
  }, []);

  useEffect(() => {
    const fetchAllPortBlocks = async () => {
      try {
        if (!ports.length) return;

        const results = await Promise.all(
          ports.map((port) => getPortByPublicId(port.publicId)),
        );

        const allBlocks: Record<number, Date[]> = {};

        results.forEach((result, index) => {
          const port = ports[index];

          const dates: Date[] = [];

          result.blocks.forEach((block) => {
            const start = new Date(block.fromDate.split("T")[0]);
            const end = new Date(block.toDate.split("T")[0]);

            // eslint-disable-next-line prefer-const
            let current = new Date(start);

            while (current <= end) {
              dates.push(
                new Date(
                  current.getFullYear(),
                  current.getMonth(),
                  current.getDate(),
                ),
              );
              current.setDate(current.getDate() + 1);
            }
          });

          allBlocks[port.portId] = dates;
        });

        setPortBlocks(allBlocks);
      } catch (e) {
        console.error(e);
      }
    };

    fetchAllPortBlocks();
  }, [ports]);

  useEffect(() => {
    const fetchOperator = async () => {
      try {
        if (!tourOperatorId) return;

        const res = await getTourOperatorWithDocuments(tourOperatorId);

        const expiry = res.partners?.find(
          (p) => p.isMainOperator,
        )?.permissionExpireDate;

        console.log("Operator expiry date:", expiry);

        if (expiry) {
          const [datePart] = expiry.split(" ");

          const [mm, dd, yyyy] = datePart.split("/").map(Number);

          const parsed = new Date(yyyy, mm - 1, dd);
          // console.log("Parsed expiry date:", parsed)

          setOperatorExpiryDate(parsed);
        }
      } catch (e) {
        console.error("Failed to fetch operator", e);
      }
    };

    fetchOperator();
  }, [tourOperatorId]);

  useEffect(() => {
    if (selectedMiqaat?.type === "package") {
      setFormData((prev) => ({
        ...prev,
        arrivalDate: selectedMiqaat.arrivalDate?.split("T")[0] || "",
        departureDate: selectedMiqaat.departureDate?.split("T")[0] || "",
      }));
    }
  }, [selectedMiqaat]);

  const getBaseRoute = (arrivalPortId: number) => {
    const najafVenue = venuesFromApi.find((v) =>
      v.venueName.toLowerCase().includes("najaf"),
    )?.venueId;

    const karbalaVenue = venuesFromApi.find((v) =>
      v.venueName.toLowerCase().includes("karbala"),
    )?.venueId;

    if (!najafVenue || !karbalaVenue) return [];

    const arrivalPort = ports.find((p) => p.portId === arrivalPortId);

    if (!arrivalPort) return [];

    const isNajafArrival = arrivalPort.portName.toLowerCase().includes("najaf");

    return isNajafArrival
      ? [najafVenue, karbalaVenue]
      : [karbalaVenue, najafVenue];
  };

  useEffect(() => {
    setIsVenueManuallyChanged(false);
  }, [formData.arrivalAirport]);

  useEffect(() => {
    if (!venuesFromApi.length || !formData.arrivalAirport) return;
    if (isVenueManuallyChanged) return;

    const route = getBaseRoute(formData.arrivalAirport);

    setFormData((prev) => ({
      ...prev,
      venues: prev.venues.slice(0, 2).map((v, i) => ({
        ...v,
        venueId: route[i] || null,
      })),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.arrivalAirport, venuesFromApi, isVenueManuallyChanged]);

  useEffect(() => {
    const fetchPorts = async () => {
      try {
        const data = await getPorts();
        setPorts(data.filter((p) => p.isActive));
      } catch (e) {
        console.error("Port fetch failed", e);
      }
    };

    fetchPorts();
  }, []);

  useEffect(() => {
    const fetchCutoffs = async () => {
      try {
        const data = await getCutoffConfigurations();
        setCutoffs(data);
      } catch (e) {
        console.error("Cutoff fetch failed", e);
      }
    };

    fetchCutoffs();
  }, []);

  useEffect(() => {
    if (!cutoffs.length) {
      setCutoffDates([]);
      return;
    }
    const dates: string[] = [];
    const formatDate = (date: Date) => {
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const dd = String(date.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    };
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    cutoffs.forEach((c) => {
      const effectiveFrom = new Date(c.effectiveFrom.split("T")[0]);
      effectiveFrom.setHours(0, 0, 0, 0);

      const effectiveTo = new Date(c.effectiveTo.split("T")[0]);
      effectiveTo.setHours(0, 0, 0, 0);
      if (today < effectiveFrom || today > effectiveTo) {
        return;
      }
      const cutoffEndDate = new Date(today);
      cutoffEndDate.setDate(today.getDate() + c.daysBeforeCutoff - 1);
      if (cutoffEndDate > effectiveTo) {
        cutoffEndDate.setTime(effectiveTo.getTime());
      }
      const current = new Date(today);
      while (current <= cutoffEndDate) {
        dates.push(formatDate(current));
        current.setDate(current.getDate() + 1);
      }
    });
    const uniqueDates = [...new Set(dates)];
    setCutoffDates(uniqueDates);
  }, [cutoffs]);

  useEffect(() => {
    const fetchIntercity = async () => {
      try {
        const data = await getIntercityCheckouts();
        setIntercityCheckouts(data.filter((i) => i.isActive));
      } catch (e) {
        console.error("Intercity fetch failed", e);
      }
    };

    fetchIntercity();
  }, []);

  useEffect(() => {
    loadTermsAndConditions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMiqaat, flowType]);

  const mapTransportMode = (mode: "flight" | "road" | "sea") => {
    if (mode === "flight") return 1;
    if (mode === "road") return 2;
    if (mode === "sea") return 3;
    return 1;
  };

  const toISOStringWithTime = (date: string, time: string) => {
    if (!date || !time || !isValidTime(time)) {
      return "";
    }

    const local = new Date(`${date}T${time}`);

    if (isNaN(local.getTime())) {
      return "";
    }

    return new Date(
      local.getTime() - local.getTimezoneOffset() * 60000,
    ).toISOString();
  };

  const isDateDisabled = (date: Date, type: "arrival" | "departure") => {
    const current = new Date(date);
    current.setHours(0, 0, 0, 0);

    if (selectedMiqaat?.type === "package") {
      const rotationStart = new Date(selectedMiqaat.arrivalDate.split("T")[0]);

      const rotationEnd = new Date(selectedMiqaat.departureDate.split("T")[0]);

      rotationStart.setHours(0, 0, 0, 0);
      rotationEnd.setHours(0, 0, 0, 0);

      return current < rotationStart || current > rotationEnd;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const minBookingDate = new Date(today);
    minBookingDate.setDate(today.getDate() + 3);

    if (current < minBookingDate) {
      return true;
    }

    if (operatorExpiryDate) {
      const expiry = new Date(operatorExpiryDate);
      expiry.setHours(0, 0, 0, 0);

      if (current > expiry) {
        return true;
      }
    }

    return false;
  };

  const getFilteredPorts = (
    mode: "flight" | "road" | "sea",
    type: "arrival" | "departure",
  ) => {
    return ports.filter((p) => {
      const mappedType = mapTypeFromApi(p.portType);

      // Transport filter
      if (mode === "flight" && mappedType !== "Air") return false;
      if (mode === "sea" && mappedType !== "Sea") return false;
      if (mode === "road" && mappedType !== "Road") return false;

      const selectedDate =
        type === "arrival" ? formData.arrivalDate : formData.departureDate;

      if (!selectedDate) return true;

      const blockedDates = portBlocks[p.portId] || [];
      const [y, m, d] = selectedDate.split("-").map(Number);
      const selected = new Date(y, m - 1, d);

      const isBlocked = blockedDates.some(
        (bd) =>
          bd.getFullYear() === selected.getFullYear() &&
          bd.getMonth() === selected.getMonth() &&
          bd.getDate() === selected.getDate(),
      );

      return !isBlocked;
    });
  };

  useEffect(() => {
    const filteredPorts = getFilteredPorts(
      formData.arrivalTransportMode,
      "arrival",
    );

    if (
      filteredPorts.length === 1 &&
      formData.arrivalAirport !== filteredPorts[0].portId
    ) {
      setFormData((prev) => ({
        ...prev,
        arrivalAirport: filteredPorts[0].portId,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.arrivalTransportMode, formData.arrivalDate, ports, portBlocks]);

  useEffect(() => {
    const filteredPorts = getFilteredPorts(
      formData.departureTransportMode,
      "departure",
    );

    if (
      filteredPorts.length === 1 &&
      formData.departureAirport !== filteredPorts[0].portId
    ) {
      setFormData((prev) => ({
        ...prev,
        departureAirport: filteredPorts[0].portId,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.departureTransportMode,
    formData.departureDate,
    ports,
    portBlocks,
  ]);

  const FLIGHT_NO_REGEX = /^(?:[A-Z]{2}|[A-Z][0-9])[0-9]{1,4}$/;
  const isValidFlightNumber = (flightNo: string) =>
    FLIGHT_NO_REGEX.test(flightNo);

  const TIME_24H_REGEX = /^([01]\d|2[0-3]):([0-5]\d)$/;
  const isValidTime = (time: string) => TIME_24H_REGEX.test(time);

  const AIRLINE_REGEX = /^[\p{L}\p{M}\p{N}\s-]{1,60}$/u;
  const isValidAirline = (value: string) => AIRLINE_REGEX.test(value.trim());

  const BOOKING_REF_REGEX = /^[A-Z0-9]{1,16}$/;
  const isValidBookingRef = (value: string) => BOOKING_REF_REGEX.test(value);

  const TOUR_NAME_REGEX = /^[A-Za-z0-9 ]{1,50}$/;

  const PARENT_TOUR_ID_REGEX = /^[A-Za-z0-9-]{1,50}$/;

  const isValidParentTourId = (value: string) =>
    PARENT_TOUR_ID_REGEX.test(value);

  const validateStep = (step: number) => {
    const newErrors: Record<string, string> = {};

    if (step === 1) {
      // ================= ARRIVAL =================
      // Arrival
      if (!formData.arrivalDate) newErrors.arrivalDate = "Date is required";
      if (!formData.arrivalTime) {
        newErrors.arrivalTime = "Time is required";
      } else if (!isValidTime(formData.arrivalTime)) {
        newErrors.arrivalTime = "Time must be in 24-hour format (HH:MM)";
      }
      if (!formData.arrivalAirport)
        newErrors.arrivalAirport = "Location is required";

      // ONLY for flight
      if (formData.arrivalTransportMode === "flight") {
        if (!formData.arrivalFlightNumber) {
          newErrors.arrivalFlightNumber = "Flight No. is required";
        } else if (!isValidFlightNumber(formData.arrivalFlightNumber)) {
          newErrors.arrivalFlightNumber = "Format must be AB1234 or A11234";
        }
      }

      if (formData.arrivalAirline && !isValidAirline(formData.arrivalAirline)) {
        newErrors.arrivalAirline =
          "Only letters, numbers, spaces and hyphens are allowed (maximum 60 characters).";
      }

      if (
        formData.arrivalBookingRef &&
        !isValidBookingRef(formData.arrivalBookingRef)
      ) {
        newErrors.arrivalBookingRef =
          "Booking Ref must be alphanumeric and max 16 characters";
      }

      // ================= DEPARTURE =================
      // Departure
      if (!formData.departureDate) newErrors.departureDate = "Date is required";
      if (!formData.departureTime) {
        newErrors.departureTime = "Time is required";
      } else if (!isValidTime(formData.departureTime)) {
        newErrors.departureTime = "Time must be in 24-hour format (HH:MM)";
      }
      if (!formData.departureAirport)
        newErrors.departureAirport = "Location is required";

      // ONLY for flight
      if (formData.departureTransportMode === "flight") {
        if (!formData.departureFlightNumber) {
          newErrors.departureFlightNumber = "Flight No. is required";
        } else if (!isValidFlightNumber(formData.departureFlightNumber)) {
          newErrors.departureFlightNumber = "Format must be AB1234 or A11234";
        }
      }

      if (
        formData.departureAirline &&
        !isValidAirline(formData.departureAirline)
      ) {
        newErrors.departureAirline =
          "Only letters, numbers, spaces and hyphens are allowed (maximum 60 characters).";
      }

      if (
        formData.departureBookingRef &&
        !isValidBookingRef(formData.departureBookingRef)
      ) {
        newErrors.departureBookingRef =
          "Booking Ref must be alphanumeric and max 16 characters";
      }
    }

    if (step === 2) {
      if (!formData.tourName.trim()) {
        newErrors.tourName = "Tour name is required";
      } else if (!TOUR_NAME_REGEX.test(formData.tourName.trim())) {
        newErrors.tourName =
          "Only letters and numbers allowed. Maximum 50 characters.";
      }
      if (
        formData.parentTourId.trim() &&
        !isValidParentTourId(formData.parentTourId.trim())
      ) {
        newErrors.parentTourId =
          "Only letters, numbers and hyphens (-) are allowed. Maximum 50 characters.";
      }
      if (!formData.noOfApplicants) {
        newErrors.noOfApplicants = "Applicants required";
      } else {
        const applicants = Number(formData.noOfApplicants);

        if (applicants < 0) {
          newErrors.noOfApplicants = "Number of applicants cannot be negative";
        } else if (applicants > 200) {
          newErrors.noOfApplicants = "Number of applicants cannot exceed 200";
        }
      }

      formData.venues.forEach((venue, index) => {
        if (!venue.venueId) newErrors[`venueName-${index}`] = "Venue required";
        if (!venue.fromDate)
          newErrors[`fromDate-${index}`] = "Check-in date required";
        if (!venue.fromTime)
          newErrors[`fromTime-${index}`] = "Check-in time required";
        if (!venue.toDate)
          newErrors[`toDate-${index}`] = "Check-out date required";
        if (!venue.toTime)
          newErrors[`toTime-${index}`] = "Check-out time required";
      });

      for (let index = 1; index < formData.venues.length; index++) {
        const previousVenue = formData.venues[index - 1];
        const currentVenue = formData.venues[index];

        if (
          previousVenue.venueId &&
          currentVenue.venueId &&
          previousVenue.venueId === currentVenue.venueId
        ) {
          const venueName =
            venuesFromApi.find((v) => v.venueId === currentVenue.venueId)
              ?.venueName || "This venue";

          toast.error(
            `Venue ${index + 1} cannot be the same as Venue ${index}. Please select a different venue instead of ${venueName}.`,
          );

          return false;
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatLocalDate = (date: Date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const totalNights = formData.venues.reduce((total, v) => {
    if (!v.fromDate || !v.toDate) return total;

    const from = new Date(v.fromDate);
    const to = new Date(v.toDate);

    const nights = Math.ceil(
      (to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24),
    );

    return total + nights;
  }, 0);

  const canAddVenue = () => {
    if (
      !formData.arrivalDate ||
      !formData.arrivalTime ||
      !formData.departureDate ||
      !formData.departureTime
    ) {
      return false;
    }

    if (formData.venues.some((v) => !v.venueId)) {
      return false;
    }

    return canAccommodateAnotherVenue({
      venues: formData.venues,
      arrivalDate: formData.arrivalDate,
      arrivalTime: formData.arrivalTime,
      departureDate: formData.departureDate,
      departureTime: formData.departureTime,
      buffers,
      intercityCheckouts,
      tourType: getTourType(),
    });
  };

  const getCalculationBreakdown = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    venue: any,
    index: number,
    type: "checkin" | "checkout",
  ) => {
    if (!venue.venueId) return null;

    const venueId = venue.venueId;

    const venueName =
      venuesFromApi.find((v) => v.venueId === venueId)?.venueName || "";

    const isLast = index === formData.venues.length - 1;

    let portId: number | undefined;

    if (index === 0) {
      portId = formData.arrivalAirport;
    } else if (isLast) {
      portId = formData.departureAirport;
    } else {
      portId = undefined;
    }

    let intercityTime: string | null = null;

    if (type === "checkout" && !isLast) {
      const venueDate = new Date(venue.toDate);

      const nextVenue = formData.venues[index + 1];

      const intercity = nextVenue?.venueId
        ? getIntercityCheckout(
            intercityCheckouts,
            venueId,
            nextVenue.venueId,
            venueDate,
            getTourType(),
          )
        : null;

      if (intercity) {
        intercityTime = intercity.checkoutTime;
      }
    }

    const isIntercityCase = type === "checkout" && !!intercityTime;

    let hasTravel = false;

    if (type === "checkin" && index > 0) {
      const prevVenue = formData.venues[index - 1];

      if (prevVenue?.venueId) {
        const intercity = getIntercityCheckout(
          intercityCheckouts,
          prevVenue.venueId,
          venue.venueId,
          new Date(prevVenue.toDate),
          getTourType(),
        );

        if (intercity?.travelTime) {
          hasTravel = true;
        }
      }
    }

    const buffersData = getTotalVenueBuffer(
      buffers,
      venueId,
      portId,
      !(isIntercityCase || hasTravel),
    );

    const accommodationBuffers = buffers.filter(
      (b) => b.venueId === venueId && b.type === "1",
    );

    const transportBuffers = !(isIntercityCase || hasTravel)
      ? buffers.filter((b) => b.venueId === venueId && b.type === "2")
      : [];

    const sumBuffers = (
      arr: BufferConfig[],
      key: "checkInBuffer" | "checkOutBuffer",
    ) =>
      arr.reduce(
        (total, b) => total + parseTimeToMinutes(b[key] || "00:00"),
        0,
      );

    const formatHours = (minutes: number) => `${Math.floor(minutes / 60)} hrs`;

    const accommodationMinutes = sumBuffers(
      accommodationBuffers,
      type === "checkin" ? "checkInBuffer" : "checkOutBuffer",
    );

    const transportMinutes = sumBuffers(
      transportBuffers,
      type === "checkin" ? "checkInBuffer" : "checkOutBuffer",
    );

    let travelMinutes = 0;

    if (type === "checkin" && index > 0) {
      const prevVenue = formData.venues[index - 1];

      if (prevVenue?.venueId) {
        const intercity =
          prevVenue?.venueId && venue.venueId
            ? getIntercityCheckout(
                intercityCheckouts,
                prevVenue.venueId,
                venue.venueId,
                new Date(prevVenue.toDate),
                getTourType(),
              )
            : null;

        if (intercity?.travelTime) {
          travelMinutes = getTravelTimeMinutes(intercity.travelTime);
        }
      }
    }

    const totalMinutes =
      (isIntercityCase
        ? accommodationMinutes
        : type === "checkin"
          ? buffersData.checkIn
          : buffersData.checkOut) + travelMinutes;

    const rawBaseTime =
      type === "checkin"
        ? index === 0
          ? formData.arrivalTime
          : venue.fromTime
        : isLast
          ? formData.departureTime
          : intercityTime
            ? intercityTime
            : venue.toTime;

    const baseTime = normalizeTime(rawBaseTime || "");

    return {
      venueName,
      baseTime,
      accommodation: formatHours(accommodationMinutes),
      transport: formatHours(transportMinutes),
      totalBuffer: formatHours(totalMinutes),
      hasTransport: !isIntercityCase && transportMinutes > 0,
      usedIntercity: isIntercityCase,
      travel: travelMinutes ? formatHours(travelMinutes) : null,
    };
  };

  const CalculationBox = ({
    data,
    type,
    index,
    isLast,
  }: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any;
    type: "checkin" | "checkout";
    index: number;
    isLast: boolean;
  }) => {
    if (!data) return null;

    const isFirst = index === 0;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const isMiddle = !isFirst && !isLast;

    let label: string | null = null;

    if (type === "checkin") {
      if (isFirst) {
        label = "Arrival Time";
      } else {
        label = null;
      }
    } else {
      // checkout
      if (data.usedIntercity) {
        label = "Intercity Checkout";
      } else if (isLast) {
        label = "Departure Time";
      } else {
        label = null;
      }
    }

    return (
      <div
        className="absolute z-10 left-0 mt-2 w-64 p-3 rounded-md bg-blue-50 border border-blue-200 text-xs text-gray-700 space-y-1 
        opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 shadow-lg pointer-events-none"
      >
        <div className="font-medium text-blue-700">Calculation Breakdown</div>
        {label && (
          <div>
            {label}: <span className="font-semibold">{data.baseTime}</span>
          </div>
        )}

        <div>
          Accommodation Buffer:{" "}
          <span className="font-semibold">{data.accommodation}</span>
        </div>

        {data.hasTransport && (
          <div>
            Transport Buffer:{" "}
            <span className="font-semibold">{data.transport}</span>
          </div>
        )}

        {data.travel && (
          <div>
            Travel Time: <span className="font-semibold">{data.travel}</span>
          </div>
        )}

        <div className="pt-1 border-t">
          Total Buffer:{" "}
          <span className="font-semibold text-blue-700">
            {data.totalBuffer}
          </span>
        </div>
      </div>
    );
  };

  const isOutsideTourRange = (date: Date) => {
    if (!formData.arrivalDate || !formData.departureDate) return false;

    const d = new Date(date);
    const start = new Date(formData.arrivalDate);
    const end = new Date(formData.departureDate);

    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    d.setHours(0, 0, 0, 0);

    return d < start || d > end;
  };

  return (
    <>
      {loading && <FullScreenLoading />}
      <div className="min-h-screen bg-background pb-6">
        <div>
          {/* Tabs */}
          <div className="mb-4 flex w-full min-w-0 flex-col gap-3 border-b border-border sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1 overflow-x-auto">
              {[
                { step: 1, label: "Arrival & Departure" },
                { step: 2, label: "Venue Summary" },
                { step: 3, label: "Confirmation" },
              ].map(({ step, label }) => {
                const isDisabled =
                  (step === 2 && !completedSteps.includes(1)) ||
                  (step === 3 && !completedSteps.includes(2));

                return (
                  <button
                    key={step}
                    disabled={isDisabled}
                    onClick={() => {
                      if (step === 1) {
                        setCurrentStep(1);
                      } else if (step === 2 && completedSteps.includes(1)) {
                        setCurrentStep(2);
                      } else if (step === 3 && completedSteps.includes(2)) {
                        setCurrentStep(3);
                      }
                    }}
                    className={`flex items-center gap-2 px-3 sm:px-5 py-2 sm:py-3 relative text-xs sm:text-sm whitespace-nowrap ${
                      isDisabled
                        ? "opacity-50 cursor-not-allowed"
                        : currentStep === step
                          ? "text-primary"
                          : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center font-semibold text-xs ${
                        currentStep === step
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {step}
                    </div>

                    <span className="font-medium">{label}</span>

                    {currentStep === step && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                    )}
                  </button>
                );
              })}
            </div>
            {!isMiqaat && (
              <div className="mb-2 flex shrink-0 items-center justify-end px-2 sm:mb-0 sm:pl-3">
                <TourTypeSwitcher
                  value={selectedTourCategory}
                  onChange={handleTourTypeChange}
                />
              </div>
            )}
          </div>

          {/* Step 1: Arrival & Departure */}
          {currentStep === 1 && (
            <div className="space-y-5">
              {/* Arrival and Departure Cards Side by Side */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                {/* Arrival Card */}
                <Card
                  className="bg-white border-2 border-primary/40 shadow-sm"
                  style={{ gap: "0px" }}
                >
                  <CardHeader className="bg-card" style={{ gap: "0px" }}>
                    <div
                      className="flex flex-wrap gap-2"
                      style={{ alignItems: "center" }}
                    >
                      <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center">
                        <ArrowRight className="w-5 h-5 text-primary-foreground" />
                      </div>
                      <CardTitle className="text-base font-semibold text-foreground">
                        Arrival Details
                      </CardTitle>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-4 pt-4 bg-card">
                    {/* Row 1: Transport Mode */}
                    <div>
                      <Label className="text-xs font-semibold mb-3 block text-muted-foreground">
                        Transport Mode
                      </Label>
                      <div className="grid grid-cols-3 gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            setFormData((prev) => ({
                              ...prev,
                              arrivalTransportMode: "flight",
                              arrivalAirport: 0,
                            }))
                          }
                          className={`flex-1 px-3 py-2 rounded-md border font-medium text-xs transition-all flex items-center justify-center gap-1.5 ${
                            formData.arrivalTransportMode === "flight"
                              ? "bg-primary border-primary text-primary-foreground"
                              : "bg-card border-border text-foreground hover:border-primary/50"
                          }`}
                        >
                          By Flight <Plane className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setFormData((prev) => ({
                              ...prev,
                              arrivalTransportMode: "sea",
                            }))
                          }
                          className={`flex-1 px-3 py-2 rounded-md border font-medium text-xs transition-all flex items-center justify-center gap-1.5 ${
                            formData.arrivalTransportMode === "sea"
                              ? "bg-primary border-primary text-primary-foreground"
                              : "bg-card border-border text-foreground hover:border-primary/50"
                          }`}
                        >
                          By Sea <Ship className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setFormData((prev) => ({
                              ...prev,
                              arrivalTransportMode: "road",
                            }))
                          }
                          className={`flex-1 px-3 py-2 rounded-md border font-medium text-xs transition-all flex items-center justify-center gap-1.5 ${
                            formData.arrivalTransportMode === "road"
                              ? "bg-primary border-primary text-primary-foreground"
                              : "bg-card border-border text-foreground hover:border-primary/50"
                          }`}
                        >
                          By Road <Truck className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Row 2: Flight No. and Time (Only for flight mode) */}
                    {formData.arrivalTransportMode === "flight" && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <SelectFieldset label="Date" required>
                            <CustomDayPicker
                              value={formData.arrivalDate}
                              operatorExpiryDate={
                                operatorExpiryDate ?? undefined
                              }
                              disabledDates={portBlockedDates}
                              cutoffDates={cutoffBlockedDates}
                              blockedDateReasons={blockedDateReasons}
                              month={arrivalCalendarMonth}
                              onMonthChange={(month) => {
                                setArrivalCalendarMonth(month);
                                setDepartureCalendarMonth(month);
                              }}
                              disabled={(date) =>
                                isDateDisabled(date, "arrival")
                              }
                              onChange={(date) => {
                                const formatted = formatLocalDate(date);
                                setArrivalCalendarMonth(date);
                                setDepartureCalendarMonth(date);

                                setFormData((prev) => ({
                                  ...prev,
                                  arrivalDate: formatted,
                                  departureDate:
                                    prev.departureDate &&
                                    new Date(prev.departureDate) <=
                                      new Date(formatted)
                                      ? ""
                                      : prev.departureDate,
                                }));
                              }}
                            />
                          </SelectFieldset>
                          {errors.arrivalDate && (
                            <p className="text-xs text-red-500 mt-1">
                              {errors.arrivalDate}
                            </p>
                          )}
                        </div>
                        <div>
                          <InputFieldset
                            label=" Flight No."
                            required
                            placeholder="IQ2021"
                            className="text-xs"
                            value={formData.arrivalFlightNumber}
                            maxLength={6}
                            onChange={(e) => {
                              let value = e.target.value.toUpperCase();

                              value = value.replace(/[^A-Z0-9]/g, "");

                              // First 2 chars must be:
                              // AA  OR  A1
                              if (value.length <= 2) {
                                if (!/^([A-Z]{0,2}|[A-Z][0-9]?)$/.test(value)) {
                                  return;
                                }
                              }

                              // After first 2 chars only digits allowed
                              if (value.length > 2) {
                                const prefix = value.substring(0, 2);
                                const digits = value.substring(2);

                                const validPrefix =
                                  /^[A-Z]{2}$/.test(prefix) ||
                                  /^[A-Z][0-9]$/.test(prefix);

                                if (!validPrefix) {
                                  return;
                                }

                                if (!/^\d{0,4}$/.test(digits)) {
                                  return;
                                }
                              }

                              setFormData((prev) => ({
                                ...prev,
                                arrivalFlightNumber: value,
                              }));
                            }}
                          />
                          {errors.arrivalFlightNumber && (
                            <p className="text-xs text-red-500 mt-1">
                              {errors.arrivalFlightNumber}
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Row 3: Airport and Date */}
                    {formData.arrivalTransportMode === "flight" && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <InputFieldset
                            label="Time (24hr Format)"
                            required
                            type="text"
                            placeholder="HH:MM"
                            maxLength={5}
                            className="text-xs"
                            value={formData.arrivalTime}
                            onChange={(e) => {
                              let value = e.target.value.replace(/[^\d]/g, "");

                              if (value.length > 4) return;

                              if (value.length >= 3) {
                                value = `${value.slice(0, 2)}:${value.slice(2)}`;
                              }

                              setFormData((prev) => ({
                                ...prev,
                                arrivalTime: value,
                              }));

                              setErrors((prev) => ({
                                ...prev,
                                arrivalTime:
                                  value.length === 5 && !isValidTime(value)
                                    ? "Time must be in 24-hour format (HH:MM)"
                                    : "",
                              }));
                            }}
                          />
                          {errors.arrivalTime && (
                            <p className="text-xs text-red-500 mt-1">
                              {errors.arrivalTime}
                            </p>
                          )}
                        </div>
                        <div>
                          <SelectFieldset
                            label={
                              formData.arrivalTransportMode === "flight"
                                ? "Airport"
                                : "Port"
                            }
                            required
                          >
                            <Select
                              key={`${formData.arrivalDate}-${formData.arrivalTransportMode}`}
                              value={
                                formData.arrivalAirport
                                  ? formData.arrivalAirport.toString()
                                  : ""
                              }
                              onValueChange={(value) => {
                                const portId = Number(value);
                                if (!formData.arrivalDate) {
                                  setFormData((prev) => ({
                                    ...prev,
                                    arrivalAirport: portId,
                                  }));
                                  return;
                                }

                                setFormData((prev) => ({
                                  ...prev,
                                  arrivalAirport: portId,
                                }));
                              }}
                            >
                              <SelectTriggerUI className="h-9 text-xs">
                                <SelectValue placeholder="Select Port" />
                              </SelectTriggerUI>

                              <SelectContent>
                                {getFilteredPorts(
                                  formData.arrivalTransportMode,
                                  "arrival",
                                ).map((port) => (
                                  <SelectItem
                                    key={port.portId}
                                    value={port.portId.toString()}
                                  >
                                    {port.portName}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </SelectFieldset>
                          {errors.arrivalAirport && (
                            <p className="text-xs text-red-500 mt-1">
                              {errors.arrivalAirport}
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Row 4: Booking Ref, Airline and Confirmed (Only for flight mode) */}
                    {formData.arrivalTransportMode === "flight" && (
                      <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
                        <div>
                          <InputFieldset
                            label="Airline"
                            type="text"
                            placeholder="Enter airline"
                            className="text-xs"
                            maxLength={60}
                            value={formData.arrivalAirline}
                            onChange={(e) => {
                              const value = e.target.value;

                              if (!/^[\p{L}\p{M}\p{N}\s-]*$/u.test(value)) {
                                return;
                              }

                              setFormData((prev) => ({
                                ...prev,
                                arrivalAirline: value,
                              }));
                            }}
                          />

                          {errors.arrivalAirline && (
                            <p className="text-xs text-red-500 mt-1">
                              {errors.arrivalAirline}
                            </p>
                          )}
                        </div>
                        <div>
                          <InputFieldset
                            label="Booking Ref"
                            placeholder="Booking Ref"
                            className="text-xs"
                            maxLength={16}
                            value={formData.arrivalBookingRef}
                            onChange={(e) => {
                              const value = e.target.value
                                .toUpperCase()
                                .replace(/[^A-Z0-9]/g, "");

                              setFormData((prev) => ({
                                ...prev,
                                arrivalBookingRef: value,
                              }));
                            }}
                            disabled={formData.arrivalConfirmed}
                          />
                          {errors.arrivalBookingRef && (
                            <p className="text-xs text-red-500 mt-1">
                              {errors.arrivalBookingRef}
                            </p>
                          )}
                        </div>
                        <div className="pb-0.5">
                          <Label className="text-xs font-semibold mb-1.5 block text-muted-foreground">
                            Confirmed
                          </Label>
                          <div className="flex items-center h-9">
                            <Switch
                              checked={formData.arrivalConfirmed}
                              onCheckedChange={(checked) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  arrivalConfirmed: checked,
                                }))
                              }
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* For sea*/}
                    {(formData.arrivalTransportMode === "sea" ||
                      formData.arrivalTransportMode === "road") && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {/* Date */}
                        <div>
                          <SelectFieldset label="Date" required>
                            <CustomDayPicker
                              value={formData.arrivalDate}
                              operatorExpiryDate={
                                operatorExpiryDate ?? undefined
                              }
                              disabledDates={portBlockedDates}
                              cutoffDates={cutoffBlockedDates}
                              blockedDateReasons={blockedDateReasons}
                              month={arrivalCalendarMonth}
                              onMonthChange={(month) => {
                                setArrivalCalendarMonth(month);
                                setDepartureCalendarMonth(month);
                              }}
                              disabled={(date) =>
                                isDateDisabled(date, "arrival")
                              }
                              onChange={(date) => {
                                const formatted = formatLocalDate(date);
                                setArrivalCalendarMonth(date);
                                setDepartureCalendarMonth(date);

                                setFormData((prev) => ({
                                  ...prev,
                                  arrivalDate: formatted,
                                  departureDate:
                                    prev.departureDate &&
                                    new Date(prev.departureDate) <=
                                      new Date(formatted)
                                      ? ""
                                      : prev.departureDate,
                                }));
                              }}
                            />
                            </SelectFieldset>
                            {errors.arrivalDate && (
                            <p className="text-xs text-red-500 mt-1">
                              {errors.arrivalDate}
                            </p>
                          )}
                        </div>

                        {/* Time */}
                        <div>
                          <InputFieldset
                            label="Time (24hr Format)"
                            required
                            type="text"
                            placeholder="HH:MM"
                            maxLength={5}
                            className="text-xs"
                            value={formData.arrivalTime}
                            onChange={(e) => {
                              let value = e.target.value.replace(/[^\d]/g, "");

                              if (value.length > 4) return;

                              if (value.length >= 3) {
                                value = `${value.slice(0, 2)}:${value.slice(2)}`;
                              }

                              setFormData((prev) => ({
                                ...prev,
                                arrivalTime: value,
                              }));

                              setErrors((prev) => ({
                                ...prev,
                                arrivalTime:
                                  value.length > 0 && !isValidTime(value)
                                    ? "Time must be in 24-hour format (HH:MM)"
                                    : "",
                              }));
                            }}
                          />
                          {errors.arrivalTime && (
                            <p className="text-xs text-red-500 mt-1">
                              {errors.arrivalTime}
                            </p>
                          )}
                        </div>

                        {/* Port */}
                        <div>
                          <SelectFieldset label="Port" required>
                            <Select
                              key={`${formData.arrivalDate}-${formData.arrivalTransportMode}`}
                              value={
                                formData.arrivalAirport
                                  ? formData.arrivalAirport.toString()
                                  : ""
                              }
                              onValueChange={(value) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  arrivalAirport: Number(value),
                                }))
                              }
                            >
                              <SelectTriggerUI className="h-9 text-xs">
                                <SelectValue placeholder="Select Port" />
                              </SelectTriggerUI>

                              <SelectContent>
                                {getFilteredPorts(
                                  formData.arrivalTransportMode,
                                  "arrival",
                                ).map((port) => (
                                  <SelectItem
                                    key={port.portId}
                                    value={port.portId.toString()}
                                  >
                                    {port.portName}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </SelectFieldset>
                          {errors.arrivalAirport && (
                            <p className="text-xs text-red-500 mt-1">
                              {errors.arrivalAirport}
                            </p>
                          )}
                        </div>

                        {/* Confirmed */}
                        <div className="flex items-center gap-2 mt-5">
                          <Switch
                            checked={formData.arrivalConfirmed}
                            onCheckedChange={(checked) =>
                              setFormData((prev) => ({
                                ...prev,
                                arrivalConfirmed: checked,
                              }))
                            }
                          />
                          <Label className="text-xs">Confirmed</Label>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Departure Card */}
                <Card
                  className="bg-white border-2 border-destructive/40 shadow-sm"
                  style={{ gap: "0px" }}
                >
                  <CardHeader className="bg-card" style={{ gap: "0px" }}>
                    <div
                      className="flex flex-wrap gap-2"
                      style={{ alignItems: "center" }}
                    >
                      <div className="w-9 h-9 rounded-full bg-destructive flex items-center justify-center">
                        <ArrowRight className="w-5 h-5 text-destructive-foreground" />
                      </div>
                      <CardTitle className="text-base font-semibold text-foreground">
                        Departure Details
                      </CardTitle>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-4 pt-4 bg-card">
                    {/* Row 1: Transport Mode */}
                    <div>
                      <Label className="text-xs font-semibold mb-3 block text-muted-foreground">
                        Transport Mode
                      </Label>
                      <div className="grid grid-cols-3 gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            setFormData((prev) => ({
                              ...prev,
                              departureTransportMode: "flight",
                              departureAirport: 0,
                            }))
                          }
                          className={`flex-1 px-3 py-2 rounded-md border font-medium text-xs transition-all flex items-center justify-center gap-1.5 ${
                            formData.departureTransportMode === "flight"
                              ? "bg-destructive border-destructive text-destructive-foreground"
                              : "bg-card border-border text-foreground hover:border-destructive/50"
                          }`}
                        >
                          By Flight <Plane className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setFormData((prev) => ({
                              ...prev,
                              departureTransportMode: "sea",
                            }))
                          }
                          className={`flex-1 px-3 py-2 rounded-md border font-medium text-xs transition-all flex items-center justify-center gap-1.5 ${
                            formData.departureTransportMode === "sea"
                              ? "bg-destructive border-destructive text-destructive-foreground"
                              : "bg-card border-border text-foreground hover:border-destructive/50"
                          }`}
                        >
                          By Sea <Ship className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setFormData((prev) => ({
                              ...prev,
                              departureTransportMode: "road",
                            }))
                          }
                          className={`flex-1 px-3 py-2 rounded-md border font-medium text-xs transition-all flex items-center justify-center gap-1.5 ${
                            formData.departureTransportMode === "road"
                              ? "bg-destructive border-destructive text-destructive-foreground"
                              : "bg-card border-border text-foreground hover:border-destructive/50"
                          }`}
                        >
                          By Road <Truck className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Row 2: Flight No. and Time (Only for flight mode) */}
                    {formData.departureTransportMode === "flight" && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <SelectFieldset label="Date" required>
                            <CustomDayPicker
                              value={formData.departureDate}
                              operatorExpiryDate={
                                operatorExpiryDate ?? undefined
                              }
                              disabledDates={portBlockedDates}
                              cutoffDates={cutoffBlockedDates}
                              blockedDateReasons={blockedDateReasons}
                              minDate={
                                formData.arrivalDate
                                  ? new Date(formData.arrivalDate)
                                  : undefined
                              }
                              month={departureCalendarMonth}
                              onMonthChange={(month) => {
                                setDepartureCalendarMonth(month);
                              }}
                              // existing validation logic
                              disabled={(date) =>
                                isDateDisabled(date, "departure")
                              }
                              onChange={(date: Date) => {
                                const formatted = formatLocalDate(date);

                                setFormData((prev) => ({
                                  ...prev,
                                  departureDate: formatted,
                                }));
                              }}
                            />
                          </SelectFieldset>
                          {errors.departureDate && (
                            <p className="text-xs text-red-500 mt-1">
                              {errors.departureDate}
                            </p>
                          )}
                        </div>
                        <div>
                          <InputFieldset
                            label="Flight No."
                            required
                            placeholder="IQ2022"
                            className="text-xs"
                            value={formData.departureFlightNumber}
                            maxLength={6}
                            onChange={(e) => {
                              let value = e.target.value.toUpperCase();

                              value = value.replace(/[^A-Z0-9]/g, "");

                              if (value.length <= 2) {
                                if (!/^([A-Z]{0,2}|[A-Z][0-9]?)$/.test(value)) {
                                  return;
                                }
                              }

                              if (value.length > 2) {
                                const prefix = value.substring(0, 2);
                                const digits = value.substring(2);

                                const validPrefix =
                                  /^[A-Z]{2}$/.test(prefix) ||
                                  /^[A-Z][0-9]$/.test(prefix);

                                if (!validPrefix) {
                                  return;
                                }

                                if (!/^\d{0,4}$/.test(digits)) {
                                  return;
                                }
                              }

                              setFormData((prev) => ({
                                ...prev,
                                departureFlightNumber: value,
                              }));
                            }}
                          />
                          {errors.departureFlightNumber && (
                            <p className="text-xs text-red-500 mt-1">
                              {errors.departureFlightNumber}
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Row 3: Airport and Date */}
                    {formData.departureTransportMode === "flight" && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <InputFieldset
                            label="Time (24hr Format)"
                            required
                            type="text"
                            placeholder="HH:MM"
                            maxLength={5}
                            className="text-xs"
                            value={formData.departureTime}
                            onChange={(e) => {
                              let value = e.target.value.replace(/[^\d]/g, "");

                              if (value.length > 4) return;

                              if (value.length >= 3) {
                                value = `${value.slice(0, 2)}:${value.slice(2)}`;
                              }

                              setFormData((prev) => ({
                                ...prev,
                                departureTime: value,
                              }));

                              setErrors((prev) => ({
                                ...prev,
                                departureTime:
                                  value.length > 0 && !isValidTime(value)
                                    ? "Time must be in 24-hour format (HH:MM)"
                                    : "",
                              }));
                            }}
                          />
                          {errors.departureTime && (
                            <p className="text-xs text-red-500 mt-1">
                              {errors.departureTime}
                            </p>
                          )}
                        </div>

                        <div>
                          <SelectFieldset
                            label={
                              formData.departureTransportMode === "flight"
                                ? "Airport"
                                : "Port"
                            }
                            required
                          >
                            <Select
                              key={`${formData.departureDate}-${formData.departureTransportMode}`}
                              value={
                                formData.departureAirport
                                  ? formData.departureAirport.toString()
                                  : ""
                              }
                              onValueChange={(value) => {
                                const portId = Number(value);
                                if (!formData.departureDate) {
                                  setFormData((prev) => ({
                                    ...prev,
                                    departureAirport: portId,
                                  }));
                                  return;
                                }

                                setFormData((prev) => ({
                                  ...prev,
                                  departureAirport: portId,
                                }));
                              }}
                            >
                              <SelectTriggerUI className="h-9 text-xs">
                                <SelectValue placeholder="Select Port" />
                              </SelectTriggerUI>

                              <SelectContent>
                                {getFilteredPorts(
                                  formData.departureTransportMode,
                                  "departure",
                                ).map((port) => (
                                  <SelectItem
                                    key={port.portId}
                                    value={port.portId.toString()}
                                  >
                                    {port.portName}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </SelectFieldset>
                          {errors.departureAirport && (
                            <p className="text-xs text-red-500 mt-1">
                              {errors.departureAirport}
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Row 4: Booking Ref, Airline and Confirmed (Only for flight mode) */}
                    {formData.departureTransportMode === "flight" && (
                      <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
                        <div>
                          <InputFieldset
                            label="Airline"
                            type="text"
                            placeholder="Enter airline"
                            className="text-xs"
                            maxLength={60}
                            value={formData.departureAirline}
                            onChange={(e) => {
                              const value = e.target.value;

                              if (!/^[\p{L}\p{M}\p{N}\s-]*$/u.test(value)) {
                                return;
                              }

                              setFormData((prev) => ({
                                ...prev,
                                departureAirline: value,
                              }));
                            }}
                          />
                          {errors.departureAirline && (
                            <p className="text-xs text-red-500 mt-1">
                              {errors.departureAirline}
                            </p>
                          )}
                        </div>
                        <div>
                          <InputFieldset
                            label="Booking Ref"
                            placeholder="Booking Ref"
                            className="text-xs"
                            maxLength={16}
                            value={formData.departureBookingRef}
                            onChange={(e) => {
                              const value = e.target.value
                                .toUpperCase()
                                .replace(/[^A-Z0-9]/g, "");

                              setFormData((prev) => ({
                                ...prev,
                                departureBookingRef: value,
                              }));
                            }}
                            disabled={formData.departureConfirmed}
                          />
                          {errors.departureBookingRef && (
                            <p className="text-xs text-red-500 mt-1">
                              {errors.departureBookingRef}
                            </p>
                          )}
                        </div>
                        <div className="pb-0.5">
                          <Label className="text-xs font-semibold mb-1.5 block text-muted-foreground">
                            Confirmed
                          </Label>
                          <div className="flex items-center h-9">
                            <Switch
                              checked={formData.departureConfirmed}
                              onCheckedChange={(checked) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  departureConfirmed: checked,
                                }))
                              }
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* For sea/road: Show Time and Confirmed */}
                    {(formData.departureTransportMode === "sea" ||
                      formData.departureTransportMode === "road") && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <SelectFieldset label="Date" required>
                            <CustomDayPicker
                              value={formData.departureDate}
                              operatorExpiryDate={
                                operatorExpiryDate ?? undefined
                              }
                              disabledDates={portBlockedDates}
                              cutoffDates={cutoffBlockedDates}
                              blockedDateReasons={blockedDateReasons}
                              minDate={
                                formData.arrivalDate
                                  ? new Date(formData.arrivalDate)
                                  : undefined
                              }
                              month={departureCalendarMonth}
                              onMonthChange={(month) => {
                                setDepartureCalendarMonth(month);
                              }}
                              disabled={(date) =>
                                isDateDisabled(date, "departure")
                              }
                              onChange={(date: Date) => {
                                const formatted = formatLocalDate(date);

                                setFormData((prev) => ({
                                  ...prev,
                                  departureDate: formatted,
                                }));
                              }}
                            />
                          </SelectFieldset>
                          {errors.departureDate && (
                            <p className="text-xs text-red-500 mt-1">
                              {errors.departureDate}
                            </p>
                          )}
                        </div>
                        <div>
                          <InputFieldset
                            label="Time (24hr Format"
                            required
                            type="text"
                            placeholder="HH:MM"
                            maxLength={5}
                            className="text-xs"
                            value={formData.departureTime}
                            onChange={(e) => {
                              let value = e.target.value.replace(/[^\d]/g, "");

                              if (value.length > 4) return;

                              if (value.length >= 3) {
                                value = `${value.slice(0, 2)}:${value.slice(2)}`;
                              }

                              setFormData((prev) => ({
                                ...prev,
                                departureTime: value,
                              }));

                              setErrors((prev) => ({
                                ...prev,
                                departureTime:
                                  value.length > 0 && !isValidTime(value)
                                    ? "Time must be in 24-hour format (HH:MM)"
                                    : "",
                              }));
                            }}
                          />
                          {errors.departureTime && (
                            <p className="text-xs text-red-500 mt-1">
                              {errors.departureTime}
                            </p>
                          )}
                        </div>

                        {/* Port */}
                        <div>
                          <SelectFieldset label="Port" required>
                            <Select
                              value={
                                formData.departureAirport
                                  ? formData.departureAirport.toString()
                                  : ""
                              }
                              onValueChange={(value) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  departureAirport: Number(value),
                                }))
                              }
                            >
                              <SelectTriggerUI className="h-9 text-xs">
                                <SelectValue placeholder="Select Port" />
                              </SelectTriggerUI>

                              <SelectContent>
                                {getFilteredPorts(
                                  formData.departureTransportMode,
                                  "departure",
                                ).map((port) => (
                                  <SelectItem
                                    key={port.portId}
                                    value={port.portId.toString()}
                                  >
                                    {port.portName}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </SelectFieldset>
                          {errors.departureAirport && (
                            <p className="text-xs text-red-500 mt-1">
                              {errors.departureAirport}
                            </p>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-2 mt-5">
                          <Switch
                            id="departure-confirmed"
                            checked={formData.departureConfirmed}
                            onCheckedChange={(checked) =>
                              setFormData((prev) => ({
                                ...prev,
                                departureConfirmed: checked,
                              }))
                            }
                          />

                          <Label
                            htmlFor="departure-confirmed"
                            className="text-xs font-medium text-foreground cursor-pointer"
                          >
                            Confirmed
                          </Label>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Navigation Footer */}
              <div
                className="flex flex-wrap items-center justify-between gap-2"
                style={{ marginTop: "10px" }}
              >
                <div></div>
                <div className="text-xs text-muted-foreground font-medium">
                  1 / 3
                </div>
                <Button
                  onClick={() => {
                    if (validateStep(1)) {
                      setCompletedSteps((prev) => [...new Set([...prev, 1])]);
                      setCurrentStep(2);
                    }
                  }}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground text-xs h-9"
                >
                  Next
                  <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Venue Summary */}
          {currentStep === 2 && (
            <div className="space-y-5">
              <Card
                className="bg-white border border-border shadow-sm"
                style={{ gap: "0px" }}
              >
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg font-semibold">
                    Venue Summary
                  </CardTitle>
                  <CardDescription className="text-sm text-muted-foreground">
                    Configure tour details, accommodation and venues
                  </CardDescription>
                </CardHeader>

                <CardContent style={{ padding: "0px 24px 20px 24px" }}>
                  <div
                    className="grid grid-cols-1 md:grid-cols-12 gap-3 border rounded-lg p-4 bg-muted/30 hover:shadow-sm transition"
                    style={{ paddingLeft: "16px", paddingRight: "16px" }}
                  >
                    {/* Tour Name */}
                    <div className="md:col-span-3 space-y-2">
                      <InputFieldset
                        label="Tour Name"
                        required
                        placeholder="Enter tour name"
                        className="text-sm"
                        maxLength={50}
                        value={formData.tourName}
                        onChange={(e) => {
                          const value = e.target.value;

                          if (value.length > 50) return;

                          if (!/^[A-Za-z0-9 ]*$/.test(value)) return;

                          setFormData((prev) => ({
                            ...prev,
                            tourName: value,
                          }));
                        }}
                      />
                      {errors.tourName && (
                        <p className="text-xs text-red-500 mt-1">
                          {errors.tourName}
                        </p>
                      )}
                    </div>

                    {/* Applicants */}
                    <div className="md:col-span-3 space-y-2">
                      <InputFieldset
                        label="No. of Applicants"
                        required
                        type="number"
                        placeholder="0"
                        min={0}
                        max={200}
                        className="text-sm"
                        value={formData.noOfApplicants}
                        onChange={(e) => {
                          const value = e.target.value;

                          if (value === "") {
                            setFormData((prev) => ({
                              ...prev,
                              noOfApplicants: "",
                            }));
                            return;
                          }

                          const num = Number(value);

                          if (num < 0 || num > 200) return;

                          setFormData((prev) => ({
                            ...prev,
                            noOfApplicants: value,
                          }));
                        }}
                      />
                      {errors.noOfApplicants && (
                        <p className="text-xs text-red-500 mt-1">
                          {errors.noOfApplicants}
                        </p>
                      )}
                    </div>

                    {/* Parent Tour Id */}
                    <div className="md:col-span-3 space-y-2">
                      <InputFieldset
                        label="Parent Tour Id"
                        placeholder="Enter Parent Tour Id"
                        className="text-sm"
                        maxLength={50}
                        value={formData.parentTourId}
                        onChange={(e) => {
                          const value = e.target.value.toUpperCase();

                          // Allow only letters, numbers and hyphen
                          if (!/^[A-Z0-9-]*$/.test(value)) {
                            return;
                          }

                          if (value.length > 50) {
                            return;
                          }

                          setFormData((prev) => ({
                            ...prev,
                            parentTourId: value,
                          }));

                          // Clear error while user is correcting the value
                          setErrors((prev) => ({
                            ...prev,
                            parentTourId: "",
                          }));
                        }}
                      />

                      {errors.parentTourId && (
                        <p className="text-xs text-red-500 mt-1">
                          {errors.parentTourId}
                        </p>
                      )}
                    </div>

                    {/* Faiz */}
                    <div className="md:col-span-3 space-y-2">
                      <Label className="text-sm font-medium text-muted-foreground">
                        Faiz Accommodation{" "}
                        <span className="text-red-500">*</span>
                      </Label>
                      <RadioGroup
                        value={formData.faizAccommodation}
                        onValueChange={(value: "yes" | "no") => {
                          if (isFaizReadOnly) return;

                          setFormData((prev) => ({
                            ...prev,
                            faizAccommodation: value,
                          }));
                        }}
                        className={`flex gap-6 items-center h-10 ${
                          isFaizReadOnly ? "pointer-events-none opacity-80" : ""
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <RadioGroupItem
                            value="yes"
                            id="faiz-yes"
                            className="
                              h-5 w-5 rounded-full
                              border-2 border-gray-200
                              flex items-center justify-center
                              data-[state=checked]:border-primary
                            "
                          >
                            <span className="h-2.5 w-2.5 rounded-full bg-primary opacity-0 data-[state=checked]:opacity-100" />
                          </RadioGroupItem>
                          <Label
                            htmlFor="faiz-yes"
                            className="text-sm cursor-pointer"
                          >
                            Yes
                          </Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <RadioGroupItem
                            value="no"
                            id="faiz-no"
                            className="
                              h-5 w-5 rounded-full
                              border-2 border-gray-200
                              flex items-center justify-center
                              data-[state=checked]:border-primary
                            "
                          >
                            <span className="h-2.5 w-2.5 rounded-full bg-primary opacity-0 data-[state=checked]:opacity-100" />
                          </RadioGroupItem>
                          <Label
                            htmlFor="faiz-no"
                            className="text-sm cursor-pointer"
                          >
                            No
                          </Label>
                        </div>
                      </RadioGroup>
                    </div>
                  </div>

                  {formData.faizAccommodation === "yes" && (
                    <Card
                      className="bg-white border-blue-200 mt-4"
                      style={{ boxShadow: "none" }}
                    >
                      <CardContent style={{ padding: "16px" }}>
                        {/* Occupancy Question */}
                        <div className="mb-4 grid grid-cols-1 lg:grid-cols-12 gap-3 items-center">
                          <div className="lg:col-span-6">
                            <Label className="text-sm font-medium text-gray-900 block">
                              Want to accommodate based on Min or Max Occupancy?
                            </Label>
                          </div>

                          <div className="lg:col-span-6">
                            <RadioGroup
                              value={accommodateBy || ""}
                              onValueChange={(v) =>
                                setAccommodateBy(v as "minimum" | "maximum")
                              }
                              className="flex gap-6 items-center"
                            >
                              {/* Minimum */}
                              <div className="flex items-center gap-2">
                                <RadioGroupItem
                                  value="minimum"
                                  id="min-occupancy"
                                  className="
                                    h-5 w-5 rounded-full
                                    border-2 border-gray-200
                                    flex items-center justify-center
                                    data-[state=checked]:border-primary
                                  "
                                >
                                  <span className="h-2.5 w-2.5 rounded-full bg-primary opacity-0 data-[state=checked]:opacity-100" />
                                </RadioGroupItem>

                                <Label
                                  htmlFor="min-occupancy"
                                  className="text-sm cursor-pointer"
                                >
                                  Min Occupancy
                                </Label>
                              </div>

                              {/* Maximum */}
                              <div className="flex items-center gap-2">
                                <RadioGroupItem
                                  value="maximum"
                                  id="max-occupancy"
                                  className="
                                    h-5 w-5 rounded-full
                                    border-2 border-gray-200
                                    flex items-center justify-center
                                    data-[state=checked]:border-primary
                                  "
                                >
                                  <span className="h-2.5 w-2.5 rounded-full bg-primary opacity-0 data-[state=checked]:opacity-100" />
                                </RadioGroupItem>

                                <Label
                                  htmlFor="max-occupancy"
                                  className="text-sm cursor-pointer"
                                >
                                  Max Occupancy
                                </Label>
                              </div>
                            </RadioGroup>
                          </div>
                        </div>
                        <div>
                          <InputFieldset
                            label="Any special accommodation request?"
                            placeholder="e.g. Wheelchair accessible, Ground floor, Adjacent rooms..."
                            className="text-sm"
                            value={specialRoomRequestDetails}
                            onChange={(e) => {
                              setSpecialRoomRequestDetails(e.target.value);

                              if (errors.specialAccommodationRequest) {
                                setErrors((prev) => ({
                                  ...prev,
                                  specialAccommodationRequest: "",
                                }));
                              }
                            }}
                          />
                          {errors.specialAccommodationRequest && (
                            <p className="text-xs text-red-500 mt-1">
                              {errors.specialAccommodationRequest}
                            </p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* ================= VENUE SECTION ================= */}
                  <div className="space-y-4" style={{ marginTop: "14px" }}>
                    {/* Section Header */}
                    <div
                      className="flex"
                      style={{
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <div>
                        <h3 className="text-sm font-semibold text-foreground">
                          Venue Schedule <span className="text-red-500">*</span>
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          Add venues with check-in and check-out details
                        </p>
                      </div>
                      {/* Add Button */}
                      <div
                        className="flex justify-end"
                        style={{ marginTop: "20px" }}
                      >
                        {canAddVenue() && (
                          <Button
                            type="button"
                            className="h-10 px-4 text-sm font-medium"
                            onClick={() => {
                              const newVenue = {
                                id: `venue-${Date.now()}`,
                                venueId: null,
                                hotelPropertyId: null,
                                hotelName: "",
                                fromDate: "",
                                fromTime: "",
                                toDate: "",
                                toTime: "",
                              };

                              setFormData((prev) => {
                                const updated = [...prev.venues];
                                updated.splice(1, 0, newVenue);

                                return {
                                  ...prev,
                                  venues: updated,
                                };
                              });
                            }}
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Venue
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Empty State */}
                    {formData.venues.length === 0 ? (
                      <div className="border border-dashed border-border rounded-lg p-10 text-center">
                        <MapPin className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                        <p className="text-sm text-muted-foreground">
                          No venues added yet
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {formData.venues.map((venue, index) => {
                          const isFirst = index === 0;
                          const isLast = index === formData.venues.length - 1;
                          return (
                            <div
                              key={venue.id}
                              className="border rounded-lg p-4 bg-muted/30 hover:shadow-sm transition"
                            >
                              <div className="flex gap-4">
                                {/* <div className="w-7 h-7 flex items-center justify-center rounded-full bg-muted text-muted-foreground text-xs font-semibold">
                                  {index + 1}
                                </div> */}
                                <div className="flex-1 space-y-4">
                                  {/* Venue Name */}
                                  <div>
                                    <div
                                      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
                                      style={{ alignItems: "center" }}
                                    >
                                      <div className="space-y-1">
                                        <SelectFieldset
                                          label={`Venue Name ${index + 1}`}
                                          required
                                          error={errors[`venueName-${index}`]}
                                        >
                                          <Select
                                            value={
                                              venue.venueId
                                                ? venue.venueId.toString()
                                                : ""
                                            }
                                            onValueChange={(value) => {
                                              const updated = [
                                                ...formData.venues,
                                              ];
                                              updated[index].venueId =
                                                Number(value);
                                              setIsVenueManuallyChanged(false);
                                              setFormData((prev) => ({
                                                ...prev,
                                                venues: updated,
                                              }));
                                            }}
                                          >
                                            <SelectTriggerUI className="h-10 text-sm">
                                              <SelectValue placeholder="Select venue">
                                                {venue.venueId
                                                  ? venuesFromApi.find(
                                                      (v) =>
                                                        v.venueId ===
                                                        venue.venueId,
                                                    )?.venueName
                                                  : "Select venue"}
                                              </SelectValue>
                                            </SelectTriggerUI>

                                            <SelectContent>
                                              {venuesFromApi.map((v) => (
                                                <SelectItem
                                                  key={v.venueId}
                                                  value={v.venueId.toString()}
                                                >
                                                  {v.venueName}
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        </SelectFieldset>
                                      </div>
                                      <div className="space-y-1">
                                        {formData.faizAccommodation ===
                                          "no" && (
                                          <SelectFieldset
                                            label={`Hotel Name ${index + 1}`}
                                          >
                                            <Select
                                              value={
                                                venue.hotelPropertyId?.toString() ??
                                                ""
                                              }
                                              onValueChange={(value) => {
                                                const selectedHotel =
                                                  hotelsByVenue[
                                                    venue.venueId ?? 0
                                                  ]?.find(
                                                    (h) =>
                                                      h.propertyId ===
                                                      Number(value),
                                                  );

                                                const updated = [
                                                  ...formData.venues,
                                                ];

                                                updated[index].hotelPropertyId =
                                                  Number(value);
                                                updated[index].hotelName =
                                                  selectedHotel?.propertyName ??
                                                  "";

                                                setFormData((prev) => ({
                                                  ...prev,
                                                  venues: updated,
                                                }));
                                              }}
                                            >
                                              <SelectTriggerUI className="h-10 text-sm">
                                                <SelectValue placeholder="Select Hotel" />
                                              </SelectTriggerUI>

                                              <SelectContent>
                                                {(
                                                  hotelsByVenue[
                                                    venue.venueId ?? 0
                                                  ] ?? []
                                                ).map((hotel) => (
                                                  <SelectItem
                                                    key={hotel.propertyId}
                                                    value={hotel.propertyId.toString()}
                                                  >
                                                    {hotel.propertyName}
                                                  </SelectItem>
                                                ))}
                                              </SelectContent>
                                            </Select>
                                          </SelectFieldset>
                                        )}
                                      </div>
                                      <div className="space-y-1"></div>
                                      <div
                                        className="space-y-1"
                                        style={{
                                          display: "flex",
                                          justifyContent: "end",
                                        }}
                                      >
                                        {/* Delete */}
                                        {!(isFirst || isLast) && (
                                          <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8 text-destructive hover:bg-destructive/10"
                                            onClick={() => {
                                              const updated =
                                                formData.venues.filter(
                                                  (_, i) => i !== index,
                                                );

                                              setIsVenueManuallyChanged(false); // allow auto recalculation

                                              setFormData((prev) => ({
                                                ...prev,
                                                venues: updated,
                                              }));
                                            }}
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </Button>
                                        )}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Dates */}
                                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                    {/* Check-In Date */}
                                    <div className="space-y-1">
                                      <SelectFieldset
                                        label="Check-In Date"
                                        required
                                        error={errors[`fromDate-${index}`]}
                                      >
                                        <CustomDayPicker
                                          value={venue.fromDate}
                                          operatorExpiryDate={
                                            operatorExpiryDate ?? undefined
                                          }
                                          mode="range"
                                          disabledDates={allBlockedDates}
                                          month={
                                            venueCalendarMonths[
                                              `checkin-${index}`
                                            ] ??
                                            (venue.fromDate
                                              ? new Date(venue.fromDate)
                                              : undefined)
                                          }
                                          onMonthChange={(month) => {
                                            setVenueCalendarMonths((prev) => ({
                                              ...prev,
                                              [`checkin-${index}`]: month,
                                            }));
                                          }}
                                          highlightRange={{
                                            from: formData.arrivalDate,
                                            to: formData.departureDate,
                                          }}
                                          disabled={(date) =>
                                            isFirst ||
                                            isDateDisabled(date, "arrival") ||
                                            isOutsideTourRange(date)
                                          }
                                          onChange={(date: Date) => {
                                            if (isFirst) return;

                                            const formatted =
                                              formatLocalDate(date);

                                            const updated = [
                                              ...formData.venues,
                                            ];
                                            updated[index].fromDate = formatted;

                                            setFormData((prev) => ({
                                              ...prev,
                                              venues: updated,
                                            }));
                                          }}
                                          placeholder="dd-MM-yyyy"
                                        />
                                      </SelectFieldset>
                                    </div>

                                    {/* Check-In Time */}
                                    <div className="space-y-1">
                                      <div className="relative group">
                                        <InputFieldset
                                          label="Check-In Time"
                                          required
                                          type="time"
                                          className={`text-sm pr-8 border border-gray-300 rounded-md transition-all outline-none ${
                                            isFirst
                                              ? "text-gray-600 border-gray-200 cursor-not-allowed opacity-70"
                                              : ""
                                          }`}
                                          value={venue.fromTime}
                                          disabled
                                          onChange={(e) => {
                                            if (isFirst) return;

                                            const updated = [
                                              ...formData.venues,
                                            ];
                                            updated[index].fromTime =
                                              e.target.value;

                                            setFormData((prev) => ({
                                              ...prev,
                                              venues: updated,
                                            }));
                                          }}
                                        />
                                        <CalculationBox
                                          type="checkin"
                                          index={index}
                                          isLast={
                                            index === formData.venues.length - 1
                                          }
                                          data={getCalculationBreakdown(
                                            venue,
                                            index,
                                            "checkin",
                                          )}
                                        />
                                      </div>
                                      {errors[`fromTime-${index}`] && (
                                        <p className="text-xs text-red-500 mt-1">
                                          {errors[`fromTime-${index}`]}
                                        </p>
                                      )}
                                    </div>

                                    {/* Check-Out Date */}
                                    <div className="space-y-1">
                                      <SelectFieldset
                                        label="Check-Out Date"
                                        required
                                        error={errors[`toDate-${index}`]}
                                      >
                                        <CustomDayPicker
                                          value={venue.toDate}
                                          operatorExpiryDate={
                                            operatorExpiryDate ?? undefined
                                          }
                                          mode="range"
                                          month={
                                            venueCalendarMonths[
                                              `checkout-${index}`
                                            ] ??
                                            (venue.toDate
                                              ? new Date(venue.toDate)
                                              : undefined)
                                          }
                                          onMonthChange={(month) => {
                                            setVenueCalendarMonths((prev) => ({
                                              ...prev,
                                              [`checkout-${index}`]: month,
                                            }));
                                          }}
                                          highlightRange={{
                                            from: formData.arrivalDate,
                                            to: formData.departureDate,
                                          }}
                                          disabledDates={allBlockedDates}
                                          disabled={(date) =>
                                            isLast ||
                                            isDateDisabled(date, "departure") ||
                                            isOutsideTourRange(date)
                                          }
                                          onChange={(date: Date) => {
                                            const formatted =
                                              formatLocalDate(date);

                                            setIsVenueManuallyChanged(true);

                                            setFormData((prev) => {
                                              const updated = [...prev.venues];
                                              updated[index].toDate = formatted;
                                              if (index < updated.length - 1) {
                                                updated[index + 1].fromDate =
                                                  formatted;
                                              }

                                              return {
                                                ...prev,
                                                venues: updated,
                                              };
                                            });
                                          }}
                                          placeholder="dd-MM-yyyy"
                                        />
                                      </SelectFieldset>
                                    </div>

                                    {/* Check-Out Time */}
                                    <div className="space-y-1">
                                      <div className="relative group">
                                        <InputFieldset
                                          label="Check-Out Time"
                                          required
                                          type="time"
                                          className={`text-sm border border-gray-300 rounded-md transition-all outline-none ${
                                            isLast
                                              ? "text-gray-600 border-gray-200 cursor-not-allowed opacity-70"
                                              : ""
                                          }`}
                                          value={venue.toTime}
                                          disabled
                                          onChange={(e) => {
                                            if (isLast) return;

                                            setIsVenueManuallyChanged(true);

                                            setFormData((prev) => {
                                              const updated = [...prev.venues];

                                              updated[index].toTime =
                                                e.target.value;
                                              if (index < updated.length - 1) {
                                                updated[index + 1].fromTime =
                                                  e.target.value;
                                              }

                                              return {
                                                ...prev,
                                                venues: updated,
                                              };
                                            });
                                          }}
                                        />
                                        <CalculationBox
                                          type="checkout"
                                          index={index}
                                          isLast={
                                            index === formData.venues.length - 1
                                          }
                                          data={getCalculationBreakdown(
                                            venue,
                                            index,
                                            "checkout",
                                          )}
                                        />
                                      </div>
                                      {errors[`toTime-${index}`] && (
                                        <p className="text-xs text-red-500 mt-1">
                                          {errors[`toTime-${index}`]}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Footer */}
              <div
                className="flex items-center justify-between"
                style={{ marginTop: "10px" }}
              >
                <Button
                  variant="outline"
                  onClick={() => setCurrentStep(1)}
                  className="bg-white text-xs h-9"
                >
                  <ArrowLeft className="w-3.5 h-3.5 mr-1.5" />
                  Previous
                </Button>
                <div className="text-xs text-muted-foreground font-medium">
                  2 / 3
                </div>
                <Button
                  onClick={() => {
                    if (validateStep(2)) {
                      setCompletedSteps((prev) => [
                        ...new Set([...prev, 1, 2]),
                      ]);
                      setCurrentStep(3);
                    }
                  }}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground text-xs h-9"
                >
                  Next
                  <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Confirmation */}
          {currentStep === 3 && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-stretch">
                <Card
                  className="bg-white w-full h-full"
                  style={{ marginTop: "0px" }}
                >
                  <CardContent className="px-6 [&:last-child]:pb-4 p-4">
                    <div className="space-y-2">
                      {/* Tour Name */}
                      <div className="flex justify-between py-2 border-b">
                        <span
                          className="text-sm font-medium text-gray-600"
                          style={{ width: "50%" }}
                        >
                          Tour Name
                        </span>
                        <span
                          className="text-xs text-gray-900"
                          style={{ width: "50%" }}
                        >
                          {formData.tourName}
                        </span>
                      </div>

                      {/* Package */}
                      <div className="flex justify-between py-2 border-b">
                        <span
                          className="text-sm font-medium text-gray-600"
                          style={{ width: "50%" }}
                        >
                          Duration
                        </span>
                        <span
                          className="text-xs text-gray-900"
                          style={{ width: "50%" }}
                        >
                          {totalNights} Days
                        </span>
                      </div>

                      {/* Tour Type */}
                      <div className="flex justify-between py-2 border-b">
                        <span
                          className="text-sm font-medium text-gray-600"
                          style={{ width: "50%" }}
                        >
                          Tour Type
                        </span>
                        <span
                          className="text-xs text-gray-900"
                          style={{ width: "50%" }}
                        >
                          {selectedMiqaat?.type === "general"
                            ? "General"
                            : `Miqaat (${selectedMiqaat?.packageName})`}
                        </span>
                      </div>

                      {/* Applicants */}
                      <div className="flex justify-between py-2 border-b">
                        <span
                          className="text-sm font-medium text-gray-600"
                          style={{ width: "50%" }}
                        >
                          Applicants
                        </span>
                        <span
                          className="text-xs text-gray-900"
                          style={{ width: "50%" }}
                        >
                          {formData.noOfApplicants} Applicants
                        </span>
                      </div>

                      {/* Faiz */}
                      <div className="flex justify-between py-2 border-b">
                        <span
                          className="text-sm font-medium text-gray-600"
                          style={{ width: "50%" }}
                        >
                          Faiz Accommodation
                        </span>
                        <span
                          className="text-xs text-gray-900"
                          style={{ width: "50%" }}
                        >
                          {formData.faizAccommodation === "yes" ? "Yes" : "No"}
                        </span>
                      </div>

                      {/* Arrival */}
                      <div className="flex justify-between py-2 border-b">
                        <span
                          className="text-sm font-medium text-gray-600"
                          style={{ width: "50%" }}
                        >
                          Arrival
                        </span>
                        <div style={{ width: "50%" }}>
                          <div className="text-xs text-gray-900">
                            {formData.arrivalDate} •{" "}
                            {
                              ports.find(
                                (p) => p.portId === formData.arrivalAirport,
                              )?.portName
                            }
                          </div>
                          <div className="text-xs text-gray-600 mt-0.5">
                            Flight {formData.arrivalFlightNumber} •{" "}
                            {formData.arrivalTime}
                          </div>
                        </div>
                      </div>

                      {/* Departure */}
                      <div className="flex justify-between py-2 border-b">
                        <span
                          className="text-sm font-medium text-gray-600"
                          style={{ width: "50%" }}
                        >
                          Departure
                        </span>
                        <div style={{ width: "50%" }}>
                          <div className="text-xs text-gray-900">
                            {formData.departureDate} •{" "}
                            {
                              ports.find(
                                (p) => p.portId === formData.departureAirport,
                              )?.portName
                            }
                          </div>
                          <div className="text-xs text-gray-600 mt-0.5">
                            Flight {formData.departureFlightNumber} •{" "}
                            {formData.departureTime}
                          </div>
                        </div>
                      </div>

                      {/* Venues */}
                      <div className="flex justify-between">
                        <span
                          className="text-sm font-medium text-gray-600"
                          style={{ width: "50%" }}
                        >
                          Venues
                        </span>
                        <div className="space-y-1" style={{ width: "50%" }}>
                          {formData.venues.map((v, i) => {
                            const venueName =
                              venuesFromApi.find((x) => x.venueId === v.venueId)
                                ?.venueName || "";

                            const nights =
                              v.fromDate && v.toDate
                                ? Math.ceil(
                                    (new Date(v.toDate).getTime() -
                                      new Date(v.fromDate).getTime()) /
                                      (1000 * 60 * 60 * 24),
                                  )
                                : 0;

                            return (
                              <div key={i} className="text-xs text-gray-900">
                                {venueName} • ({nights} nights)
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="flex h-full flex-col">
                  <Accordion
                    type="multiple"
                    defaultValue={["notes"]}
                    className="flex flex-1 flex-col w-full"
                  >
                    <AccordionItem
                      value="notes"
                      className="flex flex-1 flex-col overflow-hidden rounded-xl border border-sky-100 bg-white shadow-sm"
                    >
                      <AccordionTrigger className="px-5 py-4 hover:no-underline [&>svg]:mt-3">
                        <div className="flex items-start gap-3 text-left">
                          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sky-50 text-[#5B9BD5] shadow-sm">
                            <PencilLine className="h-5 w-5" />
                          </span>
                          <div>
                            <h2 className="text-base font-semibold text-gray-900">
                              Notes
                            </h2>
                            <p className="mt-1 text-xs font-normal text-gray-600">
                              Add any special instructions for the approval team
                              before you submit this tour.
                            </p>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="flex-1 px-5 pb-5 pt-0">
                        <textarea
                          placeholder="Write additional instructions, exceptions, or follow-up notes here..."
                          className="h-full min-h-[310px] w-full resize-none rounded-lg border border-sky-200 bg-slate-50/60 p-4 text-sm text-gray-800 outline-none transition focus:border-[#5B9BD5] focus:bg-white focus:ring-4 focus:ring-[#5B9BD5]/10"
                          value={formData.notes}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              notes: e.target.value,
                            }))
                          }
                        />
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              </div>

              <Accordion type="multiple" className="w-full space-y-4">
                <AccordionItem
                  value="terms"
                  className="overflow-hidden rounded-xl border border-sky-200 bg-white shadow-sm"
                >
                  <AccordionTrigger className="px-5 py-4 hover:no-underline [&>svg]:mt-3">
                    <div className="flex w-full flex-col gap-3 pr-3 text-left sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex items-start gap-3">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#5B9BD5]/10 text-[#5B9BD5]">
                          <BookOpenCheck className="h-5 w-5" />
                        </span>
                        <div>
                          <h2 className="text-base font-semibold text-gray-900">
                            Terms & Conditions
                          </h2>
                          <p className="mt-1 text-xs font-normal text-gray-600">
                            Review the complete terms, then tick the
                            confirmation below.
                          </p>
                        </div>
                      </div>
                      <Badge className="w-fit shrink-0 border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-50">
                        Required before submit
                      </Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-5 pb-5 pt-0">
                    <div className="max-h-[360px] overflow-y-auto rounded-lg border border-gray-200 bg-white p-4 shadow-inner">
                      {termsAndConditions ? (
                        <div
                          className="prose prose-sm max-w-none text-gray-700 [&_*]:max-w-full"
                          dangerouslySetInnerHTML={{
                            __html: termsAndConditions,
                          }}
                        />
                      ) : (
                        <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-sm text-gray-500">
                          Terms and conditions are not available right now.
                        </div>
                      )}
                    </div>

                    <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50/70 p-4 transition hover:bg-emerald-50">
                      <input
                        type="checkbox"
                        checked={termsAccepted}
                        onChange={(e) => setTermsAccepted(e.target.checked)}
                        className="mt-1 h-4 w-4 rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      <span className="flex-1">
                        <span className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                          <ShieldCheck className="h-4 w-4 text-emerald-600" />I
                          have read the terms and confirm the tour details are
                          accurate and complete
                        </span>
                        <span className="mt-1 block text-xs text-gray-600">
                          Check this only after reviewing the notes, tour
                          summary, and terms above.
                        </span>
                      </span>
                    </label>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              <div
                className="flex items-center justify-between"
                style={{ marginTop: "10px" }}
              >
                <Button
                  variant="outline"
                  onClick={() => setCurrentStep(2)}
                  className="bg-white text-xs h-9"
                >
                  <ArrowLeft className="w-3.5 h-3.5 mr-1.5" />
                  Previous
                </Button>
                <div className="text-xs text-muted-foreground font-medium">
                  3 / 3
                </div>
                <Button
                  onClick={handleSubmit}
                  disabled={!termsAccepted}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground text-xs h-9 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="w-3.5 h-3.5 mr-1.5" />
                  Create Tour
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
